import { Tooltip } from '@mm/components';
import { useClickOutside } from '@mm/hooks';
import { useWhitelabelConfig } from '@mm/hooks/src/use-whitelabel-config';
import classNames from 'classnames';
import React, { useState } from 'react';
import useRouter from 'use-react-router';
import { CHATBOT_EDITOR } from '../../../common/constants/routes';
import { trackWebAppGAEvent } from '../../../common/helpers/plugins/website-google-analytics';
import TopLevelSidebarLink from '../common/TopLevelSidebarLink/TopLevelSidebarLink';
import {
  SidebarMenuDefinition,
  SidebarTitleDefinition,
  SIDEBAR_MENU,
} from '../sidebar-menu';
import './SidebarMenuGroup.pcss';
import SidebarMenuItem from './SidebarMenuItem/SidebarMenuItem';

type Props = {
  menuGroup: SidebarTitleDefinition;
  sidebarMinimized: boolean;
  setSidebarMinimized: (isMinimized: boolean) => void;
  disableMenuItems?: boolean;
  activeChatbotId?: number;
  onMobileClick?: () => void;
  hidden?: boolean;
  isFreeTrialActive?: boolean;
  showAbsoluteSidebar?: boolean;
};

const getIsActiveMenuItem = (
  pathname: string,
  menuItem: SidebarMenuDefinition,
) => {
  const childRoutes = menuItem.childRoutes ? menuItem.childRoutes : [];
  const routes = [menuItem.url, ...childRoutes];
  return routes.some(route => pathname.indexOf(route) > -1);
};

const getDefaultExpandedId = (pathname: string) => {
  const menuGroupUrl = `/${pathname.split('/')[3]}`;

  for (const menuGroup of SIDEBAR_MENU) {
    for (const menuItem of menuGroup.menuItems) {
      if (
        menuGroupUrl.includes(menuItem.url) ||
        menuItem.url.includes(menuGroupUrl)
      ) {
        return menuGroup.id;
      }

      const childRoutes = menuItem.childRoutes || [];

      for (const childRoute of childRoutes) {
        if (menuGroupUrl.includes(childRoute)) {
          return menuGroup.id;
        }
      }
    }

    if (menuGroup.url && menuGroupUrl.includes(menuGroup.url)) {
      return menuGroup.id;
    }
  }
};

// tslint:disable-next-line: cyclomatic-complexity
export const SidebarMenuGroup = (props: Props) => {
  const {
    menuGroup,
    sidebarMinimized,
    setSidebarMinimized,
    activeChatbotId,
    onMobileClick,
    disableMenuItems,
    hidden,
    isFreeTrialActive,
    showAbsoluteSidebar,
  } = props;
  const { history, location } = useRouter();

  const { pathname } = location;
  const { pixelName = 'X-Ray Pixel' } = useWhitelabelConfig();

  const {
    id,
    menuItems,
    icon,
    text: menuGroupName,
    featureFlag,
    externalLink,
    url,
  } = menuGroup;

  const text = id === 'XRAY' ? pixelName : menuGroupName;

  const groupName = text.toLocaleLowerCase();
  const baseUrl = `${CHATBOT_EDITOR}/${activeChatbotId}`;

  const defaultExpandedGroup = getDefaultExpandedId(pathname);

  const isActive = defaultExpandedGroup === id;

  const [showMenuItems, setShowMenuItems] = useState(
    Boolean(isActive && menuItems?.length && !disableMenuItems) &&
      !sidebarMinimized,
  );

  const closeAbsoluteSidebar = () => {
    if (showAbsoluteSidebar) {
      setSidebarMinimized(true);
    }
  };
  const closeMenuItems = () => {
    if (sidebarMinimized) {
      setShowMenuItems(false);
    }
  };

  const ref = useClickOutside<HTMLDivElement>(closeMenuItems);

  const onTitleClick = (disableMenuItems?: boolean) => {
    trackWebAppGAEvent('click', {
      category: 'navigation',
      label: `navbar_link_${groupName}`,
    });

    if (externalLink) {
      window.open(url, '_blank');
    }

    if (menuItems.length) {
      return setShowMenuItems(!showMenuItems);
    }

    if (url && activeChatbotId) {
      const routeUrl = `${CHATBOT_EDITOR}/${activeChatbotId}${url}`;

      if (onMobileClick) {
        if (disableMenuItems || !menuGroup.menuItems?.length) {
          history.push(routeUrl);
          onMobileClick();
        }
      } else {
        history.push(routeUrl);
      }
    }
    closeAbsoluteSidebar();
  };

  const onMenuItemClick = () => {
    if (sidebarMinimized) {
      closeMenuItems();
    }

    closeAbsoluteSidebar();
  };

  const menuItemsClassName = classNames('sidebar-menu-group__menu-items', {
    'sidebar-menu-group__menu-items--expanded-collapsed-view':
      showMenuItems && sidebarMinimized,
    'sidebar-menu-group__menu-items--expanded-collapsed-view-menu-transformed':
      menuItems.length > 1 && sidebarMinimized,
    'sidebar-menu-group__menu-items--hidden': hidden,
    'sidebar-menu-group__menu-items--with-automations-and-free-trial':
      isFreeTrialActive,
  });

  const menuGroupClassName = classNames(
    'sidebar-menu-group',
    `${groupName}-item`,
  );

  return (
    <Tooltip title={sidebarMinimized ? text : ''} placement="right">
      <div className={menuGroupClassName} ref={ref}>
        <div
          className={classNames('sidebar-menu-group__container', {
            'sidebar-menu-group__container-active': isActive,
            'sidebar-menu-item--offseted': menuGroup.id === 'OTHER',
          })}
        >
          <TopLevelSidebarLink
            onClick={() => onTitleClick(disableMenuItems)}
            isActive={showMenuItems || isActive}
            text={text}
            iconComponent={icon}
            requiredFeature={featureFlag}
            withExtendIcons={!!menuItems?.length}
          />
        </div>

        {showMenuItems && (
          <div className={menuItemsClassName}>
            {menuItems?.map(menuItem => (
              <SidebarMenuItem
                key={menuItem.id}
                menuItem={menuItem}
                baseUrl={baseUrl}
                isActive={getIsActiveMenuItem(pathname, menuItem)}
                onClick={onMenuItemClick}
              />
            ))}
          </div>
        )}
      </div>
    </Tooltip>
  );
};
